import { useState, useEffect, useCallback, useMemo } from'react';
import { authFetch } from '../lib/authFetch';
import DateRangePicker, { getPresetDates, fmtDate } from '../components/ui/DateRangePicker';
import { useSearchParams } from'react-router-dom';
import { useAuth } from'../contexts/AuthContext';
import { supabase } from'../lib/supabase';
import {
 BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
 ResponsiveContainer, PieChart, Pie, Cell, Legend,
 ReferenceLine,
} from'recharts';
import {
 LayoutDashboard, Package, ShoppingCart, TrendingUp, Users, Download,
 Settings, RefreshCw, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
 Store, AlertTriangle,
 Mail, Calculator, DollarSign, Target, BarChart3, Percent, ArrowUpRight,
 ArrowDownRight, Eye, FileText, Bell, Zap, ExternalLink, Plus, Trash2,
 Save, CheckCircle, Copy, Info, Check, Link2,
 Search,
} from'lucide-react';
import toast from'react-hot-toast';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';
import { useFetchUsage } from '../hooks/useFetchUsage';
import { SkeletonEcommerce } from '../components/ui/Skeleton';
import { LastUpdated } from '../components/ui';

const API_BASE ='/api/shopify';
const fmt = (v, d = 2) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtUSD = (v) => `$${fmt(Math.abs(v || 0))}`;
const fmtPct = (v) => `${fmt(v || 0, 2)}%`;
const isNeg = (v) => Number(v || 0) < 0;

// ── Nav items ────────────────────────────────────────────────────────────────
const NAV = [
 { key:'dashboard', label:'Dashboard', icon: LayoutDashboard },
 { key:'products', label:'Product Analytics', icon: Package },
 { key:'orders', label:'Orders Report', icon: ShoppingCart },
 { key:'pnl', label:'P&L Report', icon: TrendingUp },
 { key:'attribution', label:'Marketing Attribution', icon: Target },
 { key:'clv', label:'Customer Lifetime Value',icon: Users },
 { key:'exports', label:'Exports', icon: Download },
];

const DATE_RANGES = [
 { v:'today', l:'Today' },
 { v:'7d', l:'Last 7 days' },
 { v:'30d', l:'Last 30 days' },
 { v:'90d', l:'Last 90 days' },
 { v:'mtd', l:'Month to date'},
 { v:'ytd', l:'Year to date' },
 { v:'custom', l:'Custom' },
];

const PIE_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#14b8a6'];

// ── Helpers ───────────────────────────────────────────────────────────────────
function localDateStr(d) {
  // Use LOCAL date parts — toISOString() converts to UTC and shifts the date
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function getDateRange(range) {
  const now   = new Date();
  const end   = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === 'today')  { /* start = end = today */ }
  else if (range === '7d')  start.setDate(start.getDate() - 7);
  else if (range === '30d') start.setDate(start.getDate() - 30);
  else if (range === '90d') start.setDate(start.getDate() - 90);
  else if (range === 'mtd') start.setDate(1);
  else if (range === 'ytd') { start.setMonth(0); start.setDate(1); }
  return {
    start: localDateStr(start),
    end:   localDateStr(end),
  };
}

// ── Delta badge ───────────────────────────────────────────────────────────────
function Delta({ value, invert = false }) {
 const n = Number(value || 0);
 const good = invert ? n < 0 : n >= 0;
 return (
 <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${good ?'text-green-600' :'text-red-500'}`}>
 {n >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
 {fmt(Math.abs(n), 2)}%
 </span>
 );
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, delta, deltaInvert = false, prefix ='', suffix ='', negative = false, sub }) {
 const display = prefix + (negative ? `(${value})` : value) + suffix;
 return (
 <div className="bg-card rounded-xl border border-border p-4">
 <p className="text-xs text-muted-foreground mb-1">{label}</p>
 <p className={`text-xl font-bold ${negative ?'text-red-600' :'text-foreground'}`}>{display}</p>
 {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
 {delta !== undefined && <div className="mt-1"><Delta value={delta} invert={deltaInvert} /></div>}
 </div>
 );
}

// ── Section header ────────────────────────────────────────────────────────────
function SectionHeader({ title, sub, actions }) {
 return (
 <div className="flex items-center justify-between mb-4">
 <div>
 <h2 className="text-base font-bold text-foreground">{title}</h2>
 {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
 </div>
 {actions && <div className="flex items-center gap-2">{actions}</div>}
 </div>
 );
}

// ── Empty placeholder ─────────────────────────────────────────────────────────
function Empty({ icon: Icon = Package, msg ='No data available', sub ='Fetch your store to populate this section.' }) {
 return (
 <div className="flex flex-col items-center justify-center py-16 text-center">
 <Icon className="h-10 w-10 text-muted-foreground/60 mb-3" />
 <p className="font-medium text-foreground">{msg}</p>
 <p className="text-sm text-muted-foreground mt-1">{sub}</p>
 </div>
 );
}

// ── Custom tooltip ────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
 if (!active || !payload?.length) return null;
 return (
 <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-xs">
 <p className="font-semibold mb-1 text-foreground">{label}</p>
 {payload.map((p, i) => (
 <p key={i} style={{ color: p.color }}>
 {p.name}: {p.name?.includes('Margin') || p.name?.includes('%') ? `${fmt(p.value, 1)}%` : `$${fmt(p.value, 0)}`}
 </p>
 ))}
 </div>
 );
};

// =============================================================================
// DASHBOARD TAB
// =============================================================================
function DashboardTab({ data, tiktokShop, syncing, onSync, dateRange, onDateChange, stores, selectedStore, onStoreChange }) {
 const d = data?.summary || {};
 const daily = data?.daily || [];
 const costBreakdown = data?.costBreakdown || [];
 const adByChannel = data?.adByChannel || [];

 // Dual-source totals — Shopify (selected store) + TikTok Shop (all shops for this user)
 const shopifyRev = parseFloat(d.revenue || 0);
 const ttShopRev  = parseFloat(tiktokShop?.net || 0);
 const combinedRev = shopifyRev + ttShopRev;
 const shopifyOrders = parseInt(d.orders || 0);
 const ttShopOrders  = parseInt(tiktokShop?.orders || 0);
 const combinedOrders = shopifyOrders + ttShopOrders;
 const ttShopShare = combinedRev > 0 ? (ttShopRev / combinedRev) * 100 : 0;

 const hasNoData = !d.orders && !d.revenue && daily.length === 0 && !tiktokShop;

 return (
 <div className="space-y-6">
 {/* No-data banner */}
 {hasNoData && (
 <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
 <div className="flex items-start gap-4">
 <div className="text-3xl">📦</div>
 <div className="flex-1">
 <p className="font-semibold text-blue-900 mb-1">No order data yet for this date range</p>
 <p className="text-sm text-blue-700 mb-3">
 Orders won't appear until you fetch them from Shopify. Click <strong>Fetch All</strong> to pull your full order history, or change the date range and click <strong>Fetch</strong>.
 </p>
 <div className="flex items-center gap-2 flex-wrap">
 <button onClick={() => onSync(true)}
 disabled={syncing}
 className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
 <RefreshCw className={`h-3.5 w-3.5 ${syncing ?'animate-spin' :''}`} />
 {syncing ?'Fetching...' :'Fetch All Orders'}
 </button>
 <span className="text-xs text-blue-600">Pulls orders from the past 12 months</span>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Alert banner */}
 {data?.alerts?.length > 0 && (
 <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-center gap-3">
 <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0" />
 <p className="text-sm text-orange-800">{data.alerts[0]}</p>
 </div>
 )}

 {/* ── Dual-source revenue card (Shopify + TikTok Shop) ────────────────
     Mirrors the Publisher dashboard's AdSense + Ad Manager dual-source
     pattern. Only renders when TikTok Shop has data — otherwise the
     Shopify-only KPIs below are the full picture. */}
 {tiktokShop && (
 <div className="bg-card rounded-2xl border border-border shadow-sm p-5">
   <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
     <div>
       <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Combined revenue · Shopify + TikTok Shop</p>
       <p className="text-2xl font-bold text-foreground mt-1">{fmtUSD(combinedRev)}</p>
       <p className="text-xs text-muted-foreground mt-0.5">{fmt(combinedOrders, 0)} orders · {fmt(ttShopShare, 1)}% from TikTok Shop</p>
     </div>
   </div>
   <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
     <div className="p-3 rounded-xl bg-muted/30 border border-border">
       <div className="flex items-center justify-between mb-1">
         <p className="text-xs text-muted-foreground flex items-center gap-1.5">🛒 Shopify</p>
         <p className="text-xs text-muted-foreground">{combinedRev > 0 ? fmt((shopifyRev / combinedRev) * 100, 1) : '0'}%</p>
       </div>
       <p className="text-lg font-semibold text-foreground">{fmtUSD(shopifyRev)}</p>
       <p className="text-xs text-muted-foreground mt-0.5">{fmt(shopifyOrders, 0)} orders</p>
     </div>
     <div className="p-3 rounded-xl bg-muted/30 border border-border">
       <div className="flex items-center justify-between mb-1">
         <p className="text-xs text-muted-foreground flex items-center gap-1.5">🛍️ TikTok Shop</p>
         <p className="text-xs text-muted-foreground">{fmt(ttShopShare, 1)}%</p>
       </div>
       <p className="text-lg font-semibold text-foreground">{fmtUSD(ttShopRev)}</p>
       <p className="text-xs text-muted-foreground mt-0.5">{fmt(ttShopOrders, 0)} orders</p>
     </div>
   </div>
 </div>
 )}

 {/* ── KPI grid — 2 cols on mobile, 3 on wide ── */}
 <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
 <KpiCard label="Orders" value={fmt(d.orders, 0)} delta={d.ordersDelta} />
 <KpiCard label="Revenue" value={fmtUSD(d.revenue)} delta={d.revenueDelta} />
 <KpiCard label="Total Costs" value={fmtUSD(d.totalCosts)} negative delta={d.totalCostsDelta} />
 <KpiCard label="Net Profit" value={fmtUSD(d.netProfit)} negative={isNeg(d.netProfit)} delta={d.netProfitDelta} />
 <KpiCard label="Net Profit Margin" value={fmtPct(d.netMargin)} negative={isNeg(d.netMargin)} delta={d.netMarginDelta} />
 <KpiCard label="ROAS"
 value={d.blendedRoas > 0 ? `${fmt(d.blendedRoas, 2)}x` : d.adSpend > 0 ?'0.00x' :'—'}
 negative={d.blendedRoas > 0 && d.blendedRoas < 1}
 delta={d.roasDelta}
 sub={d.adSpend > 0 ? `${fmtUSD(d.adSpend)} spend` :'No ad spend linked'} />
 <KpiCard label="Units Sold" value={fmt(d.units, 0)} delta={d.unitsDelta} />
 <KpiCard label="COGS" value={fmtUSD(d.cogs)} negative delta={d.cogsDelta} />
 <KpiCard label="Shipping Costs" value={fmtUSD(d.shipping)} negative delta={d.shippingDelta} />
 </div>

 {/* ── Performance chart ── */}
 <div className="bg-card rounded-xl border border-border p-5">
 <SectionHeader title="Performance" sub="Net Profit, Margin, Revenue, Total Costs" />
 {daily.length > 0 ? (
 <ResponsiveContainer width="100%" height={280}>
 <LineChart data={daily} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
 <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
 <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d?.slice(5)} />
 <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
 <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `${v.toFixed(0)}%`} />
 <Tooltip content={<ChartTooltip />} />
 <Legend />
 <Line yAxisId="left" type="monotone" dataKey="netProfit" name="Net Profit" stroke="#10b981" strokeWidth={2} dot={false} />
 <Line yAxisId="right" type="monotone" dataKey="margin" name="Net Margin %" stroke="#f59e0b" strokeWidth={2} dot={false} />
 <Line yAxisId="left" type="monotone" dataKey="revenue" name="Revenue" stroke="#3b82f6" strokeWidth={2} dot={false} />
 <Line yAxisId="left" type="monotone" dataKey="totalCosts" name="Total Costs" stroke="#ef4444" strokeWidth={2} dot={false} />
 </LineChart>
 </ResponsiveContainer>
 ) : <Empty icon={TrendingUp} msg="No performance data" />}
 </div>

 {/* ── Cost Breakdown + Ad by Channel ── */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 {/* Cost breakdown donut */}
 <div className="bg-card rounded-xl border border-border p-5">
 <SectionHeader title="Cost Breakdown" sub={`Total Costs (${isNeg(d.totalCosts) ?'-' :''}${fmtUSD(d.totalCosts)})`} />
 {costBreakdown.length > 0 ? (
 <div className="flex flex-col sm:flex-row items-center gap-4">
 <PieChart width={200} height={200}>
 <Pie data={costBreakdown} cx={95} cy={95} innerRadius={55} outerRadius={85} dataKey="value">
 {costBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
 </Pie>
 <Tooltip formatter={(v) => fmtUSD(v)} />
 </PieChart>
 <div className="space-y-2 flex-1">
 {costBreakdown.map((item, i) => (
 <div key={i} className="flex items-center justify-between text-sm">
 <div className="flex items-center gap-2">
 <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
 <span className="text-muted-foreground">{item.name}</span>
 </div>
 <div className="text-right">
 <span className="font-medium text-foreground">{fmtUSD(item.value)}</span>
 <span className="text-muted-foreground ml-2 text-xs">{fmt(item.pct, 1)}%</span>
 </div>
 </div>
 ))}
 </div>
 </div>
 ) : <Empty icon={DollarSign} msg="No cost data" />}
 </div>

 {/* Ad spend by channel */}
 <div className="bg-card rounded-xl border border-border p-5">
 <SectionHeader title="Ad Spend by Channel" />
 {adByChannel.length > 0 ? (
 <ResponsiveContainer width="100%" height={220}>
 <BarChart data={adByChannel} layout="vertical" margin={{ left: 10, right: 20 }}>
 <CartesianGrid strokeDasharray="3 3" horizontal={false} />
 <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
 <YAxis type="category" dataKey="channel" tick={{ fontSize: 11 }} width={80} />
 <Tooltip formatter={(v) => fmtUSD(v)} />
 <Bar dataKey="spend" name="Total Ad Spend" fill="#ef4444" radius={[0, 4, 4, 0]} />
 <Bar dataKey="revenue" name="Total Ad Revenue" fill="#10b981" radius={[0, 4, 4, 0]} />
 </BarChart>
 </ResponsiveContainer>
 ) : (
 <div className="flex flex-col items-center justify-center py-10 text-center">
 <Target className="h-8 w-8 text-muted-foreground/60 mb-2" />
 <p className="text-sm text-muted-foreground mb-1">Don't miss out on your best converting channels!</p>
 <p className="text-xs text-muted-foreground">Gain clarity into your customers' conversion journey with <strong>Marketing Attribution</strong>.</p>
 </div>
 )}
 </div>
 </div>

 {/* ── Ad Spend Summary ── */}
 <div className="bg-card rounded-xl border border-border p-5">
 <SectionHeader title="Ad Spend Summary" />
 <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
 <KpiCard label="Total Ad Spend" value={fmtUSD(d.adSpend)} negative delta={d.adSpendDelta} />
 <KpiCard label="POAS" value={fmt(d.poas, 2)} delta={d.poasDelta} />
 <KpiCard label="Blended ROAS" value={fmt(d.blendedRoas, 2)} delta={d.blendedRoasDelta} />
 <KpiCard label="Total Ad ROAS" value={fmt(d.adRoas, 2)} delta={d.adRoasDelta} />
 <KpiCard label="ncROAS" value={fmt(d.ncRoas, 2)} delta={d.ncRoasDelta} />
 </div>
 {/* Orders vs Ad Spend Per Order */}
 {daily.length > 0 && (
 <ResponsiveContainer width="100%" height={200}>
 <LineChart data={daily} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
 <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
 <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d?.slice(5)} />
 <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
 <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
 <Tooltip content={<ChartTooltip />} />
 <Legend />
 <Bar yAxisId="left" dataKey="orders" name="Orders" fill="#3b82f6" radius={[2, 2, 0, 0]} />
 <Line yAxisId="right" type="monotone" dataKey="adSpendPerOrder" name="Ad Spend Per Order" stroke="#f59e0b" strokeWidth={2} dot={false} />
 </LineChart>
 </ResponsiveContainer>
 )}
 </div>

 {/* ── Order + Customer Summary ── */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <div className="bg-card rounded-xl border border-border p-5">
 <SectionHeader title="Order Summary" />
 <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
 <KpiCard label="Avg. Order Value" value={fmtUSD(d.avgOrderValue)} delta={d.aovDelta} />
 <KpiCard label="Ad Spend Per Order" value={fmtUSD(d.adSpendPerOrder)} negative delta={d.asposDelta} />
 <KpiCard label="Avg. Order Profit" value={fmtUSD(d.avgOrderProfit)} negative={isNeg(d.avgOrderProfit)} delta={d.aopDelta} />
 <KpiCard label="Avg. Order Cost" value={fmtUSD(d.avgOrderCost)} negative delta={d.aocDelta} />
 <KpiCard label="Purchase Frequency" value={fmt(d.purchaseFreq, 1)} delta={d.pfDelta} />
 </div>
 </div>

 <div className="bg-card rounded-xl border border-border p-5">
 <SectionHeader title="Customer Summary" actions={<button className="text-xs text-primary hover:underline flex items-center gap-1"><Eye className="h-3 w-3" />View full report</button>} />
 <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
 <KpiCard label="Total Customers" value={fmt(d.totalCustomers, 0)} delta={d.totalCustomersDelta} />
 <KpiCard label="New Customers" value={fmt(d.newCustomers, 0)} delta={d.newCustomersDelta} />
 <KpiCard label="Repurchase Rate" value={fmtPct(d.repurchaseRate)} delta={d.repurchaseRateDelta} />
 </div>
 <div className="border-t pt-4">
 <p className="text-xs font-semibold text-muted-foreground mb-3">CAC · Customer Lifetime Value</p>
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
 {[['All-time Customers', fmt(d.allTimeCustomers, 0)], ['Revenue', fmtUSD(d.allTimeRevenue)], ['LTV', fmtUSD(d.ltv)], ['CAC', fmtUSD(d.cac)], ['LTV:CAC Ratio', `${fmt(d.ltvCacRatio, 2)} : 1`]].map(([l, v]) => (
 <div key={l}><p className="text-muted-foreground text-xs">{l}</p><p className="font-semibold text-foreground">{v}</p></div>
 ))}
 </div>
 </div>
 </div>
 </div>
 </div>
 );
}

// =============================================================================
// PRODUCT ANALYTICS TAB
// =============================================================================
function ProductsTab({ data }) {
 const products = data?.products || [];
 const [sort,      setSort]      = useState({ key:'revenue', dir:'desc' });
 const [search,    setSearch]    = useState('');
 const [sortBy,    setSortBy]    = useState('revenue');
 const [profitFilter, setProfitFilter] = useState('all'); // all | profitable | unprofitable

 const filtered = useMemo(() => {
   let p = [...products];
   if (search.trim()) p = p.filter(x => x.title?.toLowerCase().includes(search.toLowerCase()));
   if (profitFilter === 'profitable')   p = p.filter(x => (x.netProfit || 0) > 0);
   if (profitFilter === 'unprofitable') p = p.filter(x => (x.netProfit || 0) <= 0);
   return p.sort((a, b) => {
     const dir = sort.dir === 'asc' ? 1 : -1;
     return (b[sortBy] - a[sortBy]) * dir;
   });
 }, [products, search, sort, sortBy, profitFilter]);

 const sorted = filtered;

 const total = useMemo(() => products.reduce((acc, p) => ({
 revenue: acc.revenue + (p.revenue || 0),
 units: acc.units + (p.units || 0),
 cogs: acc.cogs + (p.cogs || 0),
 adSpend: acc.adSpend + (p.adSpend || 0),
 totalCosts: acc.totalCosts + (p.totalCosts || 0),
 netProfit: acc.netProfit + (p.netProfit || 0),
 grossProfit: acc.grossProfit + (p.grossProfit || 0),
 orders: acc.orders + (p.orders || 0),
 }), { revenue: 0, units: 0, cogs: 0, adSpend: 0, totalCosts: 0, netProfit: 0, grossProfit: 0, orders: 0 }), [products]);

 const COLS = [
 { key:'title', label:'Product', w:'200px', fmt: v => v },
 { key:'revenue', label:'Revenue', fmt: v => fmtUSD(v) },
 { key:'units', label:'Units', fmt: v => fmt(v, 0) },
 { key:'cogs', label:'COGS', fmt: v => `(${fmtUSD(v)})`, neg: true },
 { key:'adSpend', label:'Ad Spend', fmt: v => `(${fmtUSD(v)})`, neg: true },
 { key:'totalCosts', label:'Total Costs', fmt: v => `(${fmtUSD(v)})`, neg: true },
 { key:'netProfit', label:'Net Profit', fmt: v => v < 0 ? `-${fmtUSD(v)}` : fmtUSD(v), neg: v => v < 0 },
 { key:'netMargin', label:'Net Margin', fmt: v => fmtPct(v), neg: v => v < 0 },
 { key:'ncRoas', label:'ncROAS', fmt: v => fmt(v, 2), neg: v => v < 0 },
 { key:'grossProfit', label:'Gross Profit', fmt: v => fmtUSD(v) },
 { key:'grossMargin', label:'Gross Margin', fmt: v => fmtPct(v) },
 { key:'orders', label:'Orders', fmt: v => fmt(v, 0) },
 ];

 const th = (col) => (
 <th key={col.key} className="px-3 py-3 text-xs font-semibold text-muted-foreground text-right cursor-pointer select-none hover:text-primary whitespace-nowrap"
 style={col.w ? { minWidth: col.w, textAlign:'left' } : {}}
 onClick={() => setSort(s => ({ key: col.key, dir: s.key === col.key && s.dir ==='desc' ?'asc' :'desc' }))}>
 {col.label}
 {sort.key === col.key && (sort.dir ==='desc' ?' ↓' :' ↑')}
 </th>
 );

 return (
 <div className="space-y-4">
 <div className="bg-card rounded-xl border border-border overflow-hidden">
 <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
   <p className="text-sm font-semibold text-foreground">
     {filtered.length} of {products.length} product{products.length !== 1 ? 's' : ''}
   </p>
   {products.length > 0 && (
     <span className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
       <Info className="h-3 w-3" /> Revenue estimated
     </span>
   )}
 </div>
 {/* Filter bar */}
 <div className="px-4 py-2.5 border-b border-border bg-muted/30 flex flex-wrap gap-2 items-center">
   <div className="relative flex-1 min-w-[160px]">
     <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
     <input type="text" value={search} onChange={e => setSearch(e.target.value)}
       placeholder="Search products…"
       className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-lg
         bg-background text-foreground placeholder:text-muted-foreground
         focus:outline-none focus:ring-2 focus:ring-primary/50" />
   </div>
   <select value={sortBy} onChange={e => setSortBy(e.target.value)}
     className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50">
     <option value="revenue">Sort: Revenue</option>
     <option value="units">Sort: Units</option>
     <option value="netProfit">Sort: Net Profit</option>
     <option value="grossProfit">Sort: Gross Profit</option>
     <option value="orders">Sort: Orders</option>
   </select>
   <div className="flex rounded-lg border border-border overflow-hidden text-xs font-medium">
     {[['all','All'],['profitable','Profitable'],['unprofitable','Losing']].map(([v,l]) => (
       <button key={v} onClick={() => setProfitFilter(v)}
         className={`px-3 py-1.5 transition-colors ${
           profitFilter === v
             ? 'bg-primary text-primary-foreground'
             : 'bg-background text-foreground hover:bg-muted'
         }`}>{l}</button>
     ))}
   </div>
 </div>
 {filtered.length === 0 ? <Empty icon={Package} msg={products.length === 0 ? 'No product data' : 'No products match your filters'} /> : (
 <div className="overflow-x-auto">
 <table className="w-full text-sm">
 <thead className="bg-muted/30">
 <tr>
 {COLS.map(c => th(c))}
 </tr>
 </thead>
 <tbody className="divide-y divide-border">
 {/* Totals row — computed from filtered set */}
 <tr className="bg-blue-50 dark:bg-blue-950/30 font-semibold text-xs">
 <td className="px-3 py-2 text-foreground">Totals</td>
 {COLS.slice(1).map(c => (
 <td key={c.key} className={`px-3 py-2 text-right whitespace-nowrap ${typeof c.neg ==='function' ? (c.neg(total[c.key]) ?'text-red-600' :'text-green-600') : c.neg ?'text-red-600' :'text-foreground'}`}>
 {c.fmt ? c.fmt(total[c.key] ?? (c.key ==='netMargin' ? (total.revenue > 0 ? (total.netProfit / total.revenue * 100) : 0) : c.key ==='grossMargin' ? (total.revenue > 0 ? (total.grossProfit / total.revenue * 100) : 0) : c.key ==='ncRoas' ? 0 : 0)) : total[c.key]}
 </td>
 ))}
 </tr>
 {sorted.map((p, i) => (
 <tr key={i} className="hover:bg-muted/30">
 <td className="px-3 py-3">
 <div className="flex items-center gap-2">
 {p.imageUrl && <img src={p.imageUrl} alt="" className="w-8 h-8 rounded object-cover" />}
 <div>
 <p className="font-medium text-foreground truncate max-w-[180px]">{p.title}</p>
 <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">Active</span>
 </div>
 </div>
 </td>
 {COLS.slice(1).map(c => {
 const val = c.key ==='netMargin' ? (p.revenue > 0 ? (p.netProfit / p.revenue * 100) : 0)
 : c.key ==='grossMargin' ? (p.revenue > 0 ? (p.grossProfit / p.revenue * 100) : 0)
 : p[c.key] ?? 0;
 const neg = typeof c.neg ==='function' ? c.neg(val) : c.neg;
 return (
 <td key={c.key} className={`px-3 py-3 text-right whitespace-nowrap text-xs ${neg ?'text-red-600' :'text-foreground'}`}>
 {c.fmt ? c.fmt(val) : val}
 </td>
 );
 })}
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 </div>
 );
}

// =============================================================================
// ORDERS REPORT TAB
// =============================================================================
function OrdersTab({ data }) {
 const orders = data?.orders || [];
 const [search,   setSearch]   = useState('');
 const [status,   setStatus]   = useState('all');
 const [source,   setSource]   = useState('all');
 const [sortDir,  setSortDir]  = useState('desc');

 const sources = useMemo(() => {
   const s = new Set(orders.map(o => o.source || o.utm_source || 'direct').filter(Boolean));
   return ['all', ...Array.from(s)];
 }, [orders]);

 const filtered = useMemo(() => {
   let o = [...orders];
   if (search.trim()) o = o.filter(x =>
     (x.order_number || x.id || '').toString().includes(search) ||
     (x.customer_name || x.email || '').toLowerCase().includes(search.toLowerCase())
   );
   if (status !== 'all') o = o.filter(x => (x.fulfillment_status || x.status || '').toLowerCase() === status);
   if (source !== 'all') o = o.filter(x => (x.source || x.utm_source || 'direct') === source);
   return o.sort((a, b) => {
     const da = new Date(a.created_at || 0), db = new Date(b.created_at || 0);
     return sortDir === 'desc' ? db - da : da - db;
   });
 }, [orders, search, status, source, sortDir]);

 return (
  <div className="space-y-4">
   <div className="bg-card rounded-xl border border-border overflow-hidden">
    <div className="px-4 py-3 border-b flex items-center justify-between flex-wrap gap-2">
     <p className="text-sm font-semibold text-foreground">{filtered.length} of {orders.length} orders</p>
    </div>
    {/* Filter bar */}
    <div className="px-4 py-2.5 border-b border-border bg-muted/20 flex flex-wrap gap-2 items-center">
      <div className="relative flex-1 min-w-[180px]">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search order # or customer…"
          className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-lg
            bg-background text-foreground placeholder:text-muted-foreground
            focus:outline-none focus:ring-2 focus:ring-primary" />
      </div>
      <select value={status} onChange={e => setStatus(e.target.value)}
        className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
        <option value="all">All Status</option>
        <option value="fulfilled">Fulfilled</option>
        <option value="unfulfilled">Unfulfilled</option>
        <option value="partial">Partial</option>
        <option value="refunded">Refunded</option>
      </select>
      <select value={source} onChange={e => setSource(e.target.value)}
        className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
        {sources.map(s => <option key={s} value={s}>{s === 'all' ? 'All Sources' : s}</option>)}
      </select>
      <button onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-lg
          bg-background text-foreground hover:bg-muted transition-colors">
        {sortDir === 'desc' ? '↓ Newest' : '↑ Oldest'}
      </button>
    </div>
    {/* Legend */}
    <div className="px-4 py-2 border-b border-border flex items-center gap-3 text-xs text-muted-foreground bg-muted/10">
      <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />New</span>
      <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-blue-400" />Returning</span>
    </div>
    {filtered.length === 0 ? <Empty icon={ShoppingCart} msg={orders.length === 0 ? 'No orders yet' : 'No orders match your filters'} /> : (
     <div className="overflow-x-auto">
      <table className="w-full text-sm">
       <thead className="bg-muted/30">
        <tr>
         {['Order','Date','Customer','Type','Source','Products','Revenue','COGS','Net Profit','Margin','Status'].map(h => (
          <th key={h} className="px-3 py-3 text-xs font-semibold text-muted-foreground text-left whitespace-nowrap">{h}</th>
         ))}
        </tr>
       </thead>
       <tbody className="divide-y divide-border">
        {filtered.map((o, i) => (
         <tr key={i} className="hover:bg-muted/30">
          <td className="px-3 py-3 font-medium text-primary text-xs">#{o.orderNumber}</td>
          <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{o.date}</td>
          <td className="px-3 py-3 text-xs max-w-[120px] truncate">{o.customer}</td>
          <td className="px-3 py-3 text-xs whitespace-nowrap">
           {o.isNewCustomer === true  && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">New</span>}
           {o.isNewCustomer === false && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">Returning</span>}
           {o.isNewCustomer === null  && <span className="text-muted-foreground/60">—</span>}
          </td>
          <td className="px-3 py-3 text-xs whitespace-nowrap">
           <div className="flex flex-col gap-0.5">
            <span className={`font-medium capitalize ${o.source === 'direct' ? 'text-muted-foreground' : 'text-foreground'}`}>{o.source || 'direct'}</span>
            {o.utmCampaign && <span className="text-muted-foreground truncate max-w-[100px]">{o.utmCampaign}</span>}
           </div>
          </td>
          <td className="px-3 py-3 text-xs text-muted-foreground">{o.itemCount} items</td>
          <td className="px-3 py-3 text-xs font-medium">{fmtUSD(o.revenue)}</td>
          <td className="px-3 py-3 text-xs text-red-600">({fmtUSD(o.cogs)})</td>
          <td className={`px-3 py-3 text-xs font-medium ${isNeg(o.netProfit) ? 'text-red-600' : 'text-green-600'}`}>{fmtUSD(o.netProfit)}</td>
          <td className={`px-3 py-3 text-xs ${isNeg(o.margin) ? 'text-red-600' : 'text-green-600'}`}>{fmtPct(o.margin)}</td>
          <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${o.status === 'fulfilled' ? 'bg-green-100 text-green-700' : o.status === 'refunded' ? 'bg-red-100 text-red-700' : 'bg-muted/50 text-muted-foreground'}`}>{o.status}</span></td>
         </tr>
        ))}
       </tbody>
      </table>
     </div>
    )}
   </div>
  </div>
 );
}

// =============================================================================
// P&L REPORT TAB
// =============================================================================
function PnLTab({ data }) {
 const [groupBy, setGroupBy] = useState('monthly');
 const [metricFilter, setMetricFilter] = useState('all'); // all | revenue | profit | spend
 const daily = data?.daily || [];
 const d = data?.summary || {};

 const rows = [
 { label:'Revenue', value: d.revenue, pos: true },
 { label:' Returns / Refunds', value: -d.refunds, neg: true, indent: true },
 { label:'Net Revenue', value: d.netRevenue, pos: true, bold: true },
 { divider: true },
 { label:'COGS', value: -d.cogs, neg: true },
 { label:'Shipping Costs', value: -d.shipping, neg: true },
 { label:'Handling Fees', value: -d.handling, neg: true },
 { label:'Gross Profit', value: d.grossProfit,bold: true, pos: true },
 { label:'Gross Margin', value: d.grossMargin,pct: true },
 { divider: true },
 { label:'Total Ad Spend', value: -d.adSpend, neg: true },
 { label:'Transaction Fees', value: -d.txFees, neg: true },
 { label:'Custom Costs', value: -d.customCosts,neg: true },
 { divider: true },
 { label:'Net Profit', value: d.netProfit, bold: true, pos: !isNeg(d.netProfit), neg: isNeg(d.netProfit) },
 { label:'Net Profit Margin', value: d.netMargin, pct: true, bold: true },
 { divider: true },
 { label:'POAS', value: d.poas, plain: true },
 { label:'Blended ROAS', value: d.blendedRoas,plain: true },
 ];

 return (
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 {/* P&L statement */}
 <div className="bg-card rounded-xl border border-border overflow-hidden">
 <div className="px-4 py-3 border-b">
 <p className="font-semibold text-sm text-foreground">Profit & Loss Statement</p>
 </div>
 <div className="divide-y divide-border">
 {rows.map((r, i) => r.divider ? (
 <div key={i} className="border-t-2 border-border" />
 ) : (
 <div key={i} className={`flex items-center justify-between px-4 py-2.5 ${r.bold ?'bg-muted/30' :''}`}>
 <span className={`text-sm ${r.bold ?'font-semibold text-foreground' :'text-muted-foreground'} ${r.indent ?'pl-4' :''}`}>{r.label}</span>
 <span className={`text-sm font-medium ${r.pct ?'text-muted-foreground' : r.neg ?'text-red-600' : r.pos ?'text-green-700' :'text-foreground'}`}>
 {r.pct ? fmtPct(r.value) : r.plain ? fmt(r.value, 2) : r.neg ? `(${fmtUSD(Math.abs(r.value || 0))})` : fmtUSD(r.value)}
 </span>
 </div>
 ))}
 </div>
 </div>

 {/* Daily P&L chart */}
 <div className="bg-card rounded-xl border border-border p-5">
 <SectionHeader title="Daily P&L" />
 {daily.length > 0 ? (
 <ResponsiveContainer width="100%" height={350}>
 <BarChart data={daily} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
 <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
 <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d?.slice(5)} />
 <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
 <Tooltip content={<ChartTooltip />} />
 <Legend />
 <Bar dataKey="revenue" name="Revenue" fill="#3b82f6" stackId="a" radius={[0, 0, 0, 0]} />
 <Bar dataKey="netProfit" name="Net Profit" fill="#10b981" stackId="b" radius={[2, 2, 0, 0]} />
 <Bar dataKey="totalCosts" name="Total Costs" fill="#ef4444" stackId="c" radius={[2, 2, 0, 0]} />
 </BarChart>
 </ResponsiveContainer>
 ) : <Empty icon={TrendingUp} msg="No P&L data" />}
 </div>
 </div>
 );
}

// =============================================================================
// MARKETING ATTRIBUTION TAB
// =============================================================================
function AttributionTab({ data }) {
  const channels = data?.attribution || [];
  // hasOrders: derive from channels (which come from shopify_orders directly)
  // NOT from summary.orders (which needs shopify_daily_analytics to be populated).
  // If channels has data, orders clearly exist. If channels is empty, we still
  // check the summary as a secondary signal.
  const hasOrders = channels.length > 0 || (data?.summary?.orders || 0) > 0;
  const totalChannelOrders = channels.reduce((s, c) => s + (c.orders || 0), 0);
  const totalChannelRevenue = channels.reduce((s, c) => s + (c.revenue || 0), 0);

  // ── UTM Builder state ────────────────────────────────────────────────────
  const [utm, setUtm] = useState({ website: '', source: '', medium: '', campaign: '', content: '', term: '' });
  const [copied, setCopied] = useState(false);

  const PRESETS = [
    { label: 'Facebook Ads', source: 'facebook',  medium: 'cpc',   medium2: '' },
    { label: 'Google Ads',   source: 'google',    medium: 'cpc',   medium2: '' },
    { label: 'TikTok Ads',   source: 'tiktok',    medium: 'cpc',   medium2: '' },
    { label: 'Instagram',    source: 'instagram', medium: 'cpc',   medium2: '' },
    { label: 'Email',        source: 'email',     medium: 'email', medium2: '' },
    { label: 'SMS',          source: 'sms',       medium: 'sms',   medium2: '' },
  ];

  const applyPreset = (p) => setUtm(u => ({ ...u, source: p.source, medium: p.medium }));

  const buildUrl = () => {
    const base = utm.website.trim();
    if (!base) return '';
    try {
      const url = new URL(base.startsWith('http') ? base : 'https://' + base);
      if (utm.source)   url.searchParams.set('utm_source',   utm.source.trim());
      if (utm.medium)   url.searchParams.set('utm_medium',   utm.medium.trim());
      if (utm.campaign) url.searchParams.set('utm_campaign', utm.campaign.trim());
      if (utm.content)  url.searchParams.set('utm_content',  utm.content.trim());
      if (utm.term)     url.searchParams.set('utm_term',     utm.term.trim());
      return url.toString();
    } catch { return ''; }
  };

  const generatedUrl = buildUrl();

  const copyUrl = () => {
    if (!generatedUrl) return;
    navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const field = (key, label, placeholder, required = false) => (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <input
        value={utm[key]}
        onChange={e => setUtm(u => ({ ...u, [key]: e.target.value }))}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-card"
      />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* ── Attribution table — always shown ── */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex gap-2 text-sm text-blue-700">
        <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-blue-500" />
        <span>Shows <strong>all orders ever synced</strong> grouped by UTM source. Orders with no UTM tag appear as <strong>direct</strong>. Date range selector does not apply here — use Fetch All to pull full history.</span>
      </div>

      {/* ── Summary KPIs ── */}
      {channels.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-card rounded-xl border border-border p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Orders</p>
            <p className="text-xl font-bold text-foreground">{fmt(totalChannelOrders, 0)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{channels.length} channel{channels.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Revenue</p>
            <p className="text-xl font-bold text-foreground">{fmtUSD(totalChannelRevenue)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">all channels</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-4">
            <p className="text-xs text-muted-foreground mb-1">Direct / No UTM</p>
            <p className="text-xl font-bold text-foreground">{fmt(channels.find(c => c.channel === 'direct')?.orders || 0, 0)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{totalChannelOrders > 0 ? fmt(((channels.find(c => c.channel === 'direct')?.orders || 0) / totalChannelOrders) * 100, 1) : '0'}% of orders</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-4">
            <p className="text-xs text-muted-foreground mb-1">Paid Channels</p>
            <p className="text-xl font-bold text-foreground">{channels.filter(c => !['direct','organic','email','sms','referral'].includes(c.channel)).length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">tracked sources</p>
          </div>
        </div>
      )}

      {/* ── Channel table ── */}
      {channels.length > 0 ? (
        <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div>
              <p className="font-semibold text-sm text-foreground">Channel Attribution</p>
              <p className="text-xs text-muted-foreground mt-0.5">{channels.length} source{channels.length !== 1 ? 's' : ''} · all-time · sorted by revenue</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr>{['Channel / Source', 'Orders', 'New Cust.', 'Returning', 'Revenue', 'Ad Spend', 'ROAS', 'Net Profit', 'Share'].map(h => (
                  <th key={h} className="px-4 py-3 text-xs font-semibold text-muted-foreground text-left whitespace-nowrap">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-border">
                {channels.map((ch, i) => {
                  const share = totalChannelRevenue > 0 ? (ch.revenue / totalChannelRevenue) * 100 : 0;
                  const isDirect = ch.channel?.toLowerCase() === 'direct';
                  const isPaid = !['direct', 'organic', 'email', 'sms', 'referral'].includes(ch.channel?.toLowerCase());
                  const newPct = ch.newPct || 0;
                  const retPct = ch.returningPct || 0;
                  return (
                    <tr key={i} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium capitalize text-foreground">{ch.channel}</span>
                          {isDirect && <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted/50 text-muted-foreground">no UTM</span>}
                          {isPaid && !isDirect && <span className="text-xs px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600">paid</span>}
                          {!isPaid && !isDirect && <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-50 text-green-600">{ch.channel?.toLowerCase()}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-foreground font-medium">{fmt(ch.orders, 0)}</td>
                      <td className="px-4 py-3">
                        {ch.newCustomers > 0
                          ? <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{fmt(ch.newCustomers, 0)} <span className="opacity-70">({fmt(newPct, 0)}%)</span></span>
                          : <span className="text-muted-foreground/60 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {ch.returningCustomers > 0
                          ? <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">{fmt(ch.returningCustomers, 0)} <span className="opacity-70">({fmt(retPct, 0)}%)</span></span>
                          : <span className="text-muted-foreground/60 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 font-medium">{fmtUSD(ch.revenue)}</td>
                      <td className="px-4 py-3 text-red-600">{ch.spend > 0 ? `(${fmtUSD(ch.spend)})` : <span className="text-muted-foreground/60">—</span>}</td>
                      <td className="px-4 py-3 font-medium">{ch.roas > 0 ? fmt(ch.roas, 2) + 'x' : <span className="text-muted-foreground/60">—</span>}</td>
                      <td className={`px-4 py-3 font-medium ${isNeg(ch.profit) ? 'text-red-600' : 'text-green-600'}`}>{fmtUSD(ch.profit)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-muted/50 rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(share, 100)}%` }} />
                          </div>
                          <span className="text-xs text-muted-foreground">{fmt(share, 1)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <Target className="h-12 w-12 text-muted-foreground/60 mx-auto mb-3" />
          <p className="font-medium text-foreground">No order data found</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
            Click <strong>Fetch All</strong> to pull your Shopify orders. Every order — including those without UTM tags — will appear here as <strong>direct</strong>.
          </p>
          <button
            onClick={() => document.querySelector('[title="Fetch all orders from the past 12 months"]')?.click()}
            className="mt-4 px-5 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90"
          >
            Fetch All Orders
          </button>
        </div>
      )}

      {/* ── UTM Builder ────────────────────────────────────────────────────── */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Link2 className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-sm text-foreground">UTM Link Builder</h3>
          <span className="ml-auto text-xs text-muted-foreground">Build tagged URLs — Shopify captures these at checkout</span>
        </div>
        <div className="p-5 space-y-4">

          {/* Presets */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">Quick presets</p>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map(p => (
                <button key={p.label} onClick={() => applyPreset(p)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:border-primary hover:text-primary transition-colors">
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field('website',  'Website URL',      'https://your-store.com/products/serum', true)}
            {field('campaign', 'Campaign Name',    'summer_sale_2025',                      true)}
            {field('source',   'Source',           'facebook',                              true)}
            {field('medium',   'Medium',           'cpc'                                        )}
            {field('content',  'Content (optional)','video_ad_v1'                               )}
            {field('term',     'Term (optional)',   'face+serum'                                 )}
          </div>

          {/* Generated URL */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Generated URL</p>
            <div className={`flex items-center gap-2 p-3 rounded-xl border text-xs font-mono break-all min-h-[44px] ${
              generatedUrl ? 'bg-muted/30 border-border text-foreground' : 'bg-muted/30 border-dashed border-border text-muted-foreground'
            }`}>
              <span className="flex-1">{generatedUrl || 'Fill in the fields above to generate your URL'}</span>
              {generatedUrl && (
                <button onClick={copyUrl} className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  copied ? 'bg-green-100 text-green-700' : 'bg-primary text-white hover:bg-primary/90'
                }`}>
                  {copied ? <><Check className="h-3.5 w-3.5" /> Copied!</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
                </button>
              )}
            </div>
          </div>

          {/* How it works */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800 space-y-1.5">
            <p className="font-semibold flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> How RevHapi captures this</p>
            <p>1. Customer clicks your tagged link → lands on your Shopify store with UTM params in the URL.</p>
            <p>2. Shopify's checkout saves UTM params in the order's <code className="bg-amber-100 px-1 rounded">customAttributes</code> field.</p>
            <p>3. When you sync orders, RevHapi reads <code className="bg-amber-100 px-1 rounded">customAttributes</code> and stores <code className="bg-amber-100 px-1 rounded">utm_source</code>, <code className="bg-amber-100 px-1 rounded">utm_medium</code>, <code className="bg-amber-100 px-1 rounded">utm_campaign</code> on each order row.</p>
            <p>4. The Attribution tab above groups those orders by source so you can see revenue and ROAS per channel.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// CUSTOMER LIFETIME VALUE TAB
// =============================================================================
function CLVTab({ data }) {
 const [segment, setSegment] = useState('all'); // all | high | medium | low | at_risk
 const [sortBy,  setSortBy]  = useState('ltv');  // ltv | orders | aov | days_since
  const cohorts  = data?.cohorts || [];
  const overview = data?.clvOverview || {};
  const hasData  = cohorts.length > 0 || (overview.allTimeCustomers || 0) > 0;

  const [activeMetric, setActiveMetric] = useState('ltv');

  if (!hasData) return (
    <div className="space-y-4">
      <div className="bg-card rounded-xl border border-border p-12 text-center">
        <Users className="h-12 w-12 text-muted-foreground/60 mx-auto mb-3" />
        <p className="font-medium text-foreground">No customer data yet</p>
        <p className="text-sm text-muted-foreground mt-1">CLV data uses all historical orders regardless of the date range selected above.</p>
      </div>
      {/* DEBUG — remove after fix */}
      <div className="bg-card text-muted-foreground/60 rounded-xl p-4 text-xs font-mono">
        <p className="text-yellow-400 font-semibold mb-2">CLV Debug (remove after fix)</p>
        <p>data keys: {Object.keys(data || {}).join(', ') || 'none'}</p>
        <p>clvOverview: {JSON.stringify(overview)}</p>
        <p>cohorts length: {cohorts.length}</p>
        <p>allTimeCustomers: {overview.allTimeCustomers}</p>
        <p>_debug: {JSON.stringify(overview._debug)}</p>
      </div>
    </div>
  );

  const ltvCacRatio = overview.ltvCacRatio || (overview.cac > 0 ? overview.ltv / overview.cac : 0);
  const ratioColor  = ltvCacRatio >= 3 ? 'text-green-600' : ltvCacRatio >= 1.5 ? 'text-yellow-600' : 'text-red-500';
  const ratioBg     = ltvCacRatio >= 3 ? 'bg-green-50 border-green-200' : ltvCacRatio >= 1.5 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200';
  const ratioLabel  = ltvCacRatio >= 3 ? 'Excellent' : ltvCacRatio >= 1.5 ? 'Healthy' : 'Needs improvement';

  const chartData = cohorts.map(c => ({
    month: c.cohort?.slice(0, 7) || c.cohort,
    LTV:   parseFloat(c.ltv?.toFixed(2) || 0),
    CAC:   parseFloat(c.cac?.toFixed(2) || 0),
    Revenue: parseFloat(c.revenue?.toFixed(2) || 0),
    'Repurchase Rate': parseFloat(c.repurchaseRate?.toFixed(1) || 0),
    'LTV:CAC': parseFloat(c.ltvCacRatio?.toFixed(2) || 0),
  }));

  const metricOptions = [
    { key: 'ltv', label: 'LTV', color: '#10b981' },
    { key: 'cac', label: 'CAC', color: '#f59e0b' },
    { key: 'ratio', label: 'LTV:CAC', color: '#6366f1' },
  ];

  const fmtCohortMonth = (m) => {
    if (!m) return m;
    const [y, mo] = m.split('-');
    return new Date(parseInt(y), parseInt(mo) - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  return (
    <div className="space-y-6">

      {/* ── LTV:CAC Hero ── */}
      <div className={`rounded-xl border p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 ${ratioBg}`}>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <span className={`text-4xl font-bold ${ratioColor}`}>{ltvCacRatio.toFixed(2)} : 1</span>
            <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${ratioBg} ${ratioColor}`}>{ratioLabel}</span>
          </div>
          <p className="text-sm text-muted-foreground font-medium">LTV : CAC Ratio</p>
          <p className="text-xs text-muted-foreground mt-1">
            {ltvCacRatio >= 3
              ? 'Great — you earn €' + (overview.ltv || 0).toFixed(0) + ' per customer and spend €' + (overview.cac || 0).toFixed(0) + ' to acquire them.'
              : ltvCacRatio >= 1.5
              ? "Healthy but there's room to improve. Aim for 3:1 by increasing LTV or reducing acquisition spend."
              : 'Your acquisition cost is close to or exceeds customer lifetime value. Focus on improving retention and LTV.'}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-muted-foreground mb-1">Benchmark</p>
          <div className="space-y-0.5 text-xs">
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" /><span className="text-muted-foreground">3:1+ Excellent</span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" /><span className="text-muted-foreground">1.5:1–3:1 Healthy</span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" /><span className="text-muted-foreground">&lt;1.5:1 At risk</span></div>
          </div>
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: 'Customers',       value: fmt(overview.allTimeCustomers, 0),                      sub: 'all-time unique' },
          { label: 'Repeat buyers',   value: fmt(overview.repeatCustomers, 0),                       sub: fmtPct(overview.repurchaseRate) + ' repurchase rate' },
          { label: 'Total revenue',   value: fmtUSD(overview.revenue),                               sub: 'lifetime' },
          { label: 'Avg LTV',         value: fmtUSD(overview.ltv),                                   sub: 'per customer', highlight: true },
          { label: 'Avg CAC',         value: overview.cac > 0 ? fmtUSD(overview.cac) : '—',          sub: 'to acquire' },
          { label: 'Avg order value', value: fmtUSD(overview.avgOrderValue),                         sub: fmt(overview.totalOrders, 0) + ' total orders' },
          { label: 'Orders / cust.',  value: (overview.avgOrdersPerCustomer || 0).toFixed(1),         sub: 'avg per customer' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl border p-3.5 ${s.highlight ? 'border-green-200 bg-green-50' : 'border-border bg-card'}`}>
            <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
            <p className={`text-lg font-bold ${s.highlight ? 'text-green-700' : 'text-foreground'}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* ── Chart ── */}
      {chartData.length > 1 && (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-semibold text-sm text-foreground">LTV & CAC by acquisition cohort</p>
              <p className="text-xs text-muted-foreground mt-0.5">Revenue generated per new customer acquired each month vs what it cost to acquire them</p>
            </div>
            <div className="flex gap-1">
              {metricOptions.map(m => (
                <button key={m.key} onClick={() => setActiveMetric(m.key)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${activeMetric === m.key ? 'text-white' : 'text-muted-foreground hover:bg-muted/50'}`}
                  style={activeMetric === m.key ? { backgroundColor: m.color } : {}}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={220}>
              {activeMetric === 'ratio' ? (
                <LineChart data={chartData} margin={{ left: 0, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={fmtCohortMonth} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v + 'x'} />
                  <Tooltip formatter={v => [v + 'x', 'LTV:CAC']} labelFormatter={fmtCohortMonth} />
                  <Line type="monotone" dataKey="LTV:CAC" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  <ReferenceLine y={3} stroke="#10b981" strokeDasharray="5 5" label={{ value: '3x target', position: 'right', fontSize: 10, fill: '#10b981' }} />
                </LineChart>
              ) : (
                <BarChart data={chartData} margin={{ left: 0, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={fmtCohortMonth} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => '€' + v} />
                  <Tooltip formatter={(v, n) => ['€' + Number(v).toFixed(2), n]} labelFormatter={fmtCohortMonth} />
                  {activeMetric === 'ltv' && <>
                    <Bar dataKey="LTV" fill="#10b981" radius={[4,4,0,0]} />
                    <Bar dataKey="CAC" fill="#d1fae5" radius={[4,4,0,0]} />
                  </>}
                  {activeMetric === 'cac' && <>
                    <Bar dataKey="CAC" fill="#f59e0b" radius={[4,4,0,0]} />
                    <Bar dataKey="LTV" fill="#fef3c7" radius={[4,4,0,0]} />
                  </>}
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Cohort table ── */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <p className="font-semibold text-sm text-foreground">Monthly cohort breakdown</p>
          <p className="text-xs text-muted-foreground mt-0.5">Each row = customers first acquired that month and all revenue they've generated since</p>
        </div>
        {cohorts.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No cohort data — fetch orders first</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr>
                  {[
                    { h: 'Month',        align: 'left'  },
                    { h: 'New customers',align: 'right' },
                    { h: 'Total orders', align: 'right' },
                    { h: 'Revenue',      align: 'right' },
                    { h: 'Avg LTV',      align: 'right' },
                    { h: 'Avg AOV',      align: 'right' },
                    { h: 'Avg CAC',      align: 'right' },
                    { h: 'LTV:CAC',      align: 'right' },
                  ].map(({ h, align }) => (
                    <th key={h} className={`px-4 py-3 text-xs font-semibold text-muted-foreground text-${align} whitespace-nowrap`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {cohorts.map((c, i) => {
                  const ratio = c.ltvCacRatio || 0;
                  const ratioClass = ratio >= 3 ? 'text-green-600 font-semibold' : ratio >= 1.5 ? 'text-yellow-600' : ratio > 0 ? 'text-red-500' : 'text-muted-foreground';
                  return (
                    <tr key={i} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">{fmtCohortMonth(c.cohort)}</td>
                      <td className="px-4 py-3 text-right">{fmt(c.newCustomers || 0, 0)}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{fmt(c.totalOrders || 0, 0)}</td>
                      <td className="px-4 py-3 text-right">{fmtUSD(c.revenue)}</td>
                      <td className="px-4 py-3 text-right font-medium text-green-600">{fmtUSD(c.ltv)}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{fmtUSD(c.aov)}</td>
                      <td className="px-4 py-3 text-right">{c.cac > 0 ? fmtUSD(c.cac) : <span className="text-muted-foreground/60">—</span>}</td>
                      <td className={`px-4 py-3 text-right ${ratioClass}`}>
                        {ratio > 0 ? ratio.toFixed(2) + ' : 1' : <span className="text-muted-foreground/60">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-muted/30 border-t border-border">
                <tr>
                  <td className="px-4 py-3 font-semibold text-xs text-muted-foreground">All-time total</td>
                  <td className="px-4 py-3 text-right font-semibold">{fmt(overview.allTimeCustomers, 0)}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{fmt(overview.totalOrders, 0)}</td>
                  <td className="px-4 py-3 text-right font-semibold">{fmtUSD(overview.revenue)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-green-600">{fmtUSD(overview.ltv)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-muted-foreground">{fmtUSD(overview.avgOrderValue)}</td>
                  <td className="px-4 py-3 text-right font-semibold">{overview.cac > 0 ? fmtUSD(overview.cac) : '—'}</td>
                  <td className={`px-4 py-3 text-right font-semibold ${ratioColor}`}>{ltvCacRatio.toFixed(2)} : 1</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── Insights ── */}
      {hasData && (
        <div className="bg-card rounded-xl border border-border p-5">
          <p className="font-semibold text-sm text-foreground mb-3">Insights & recommendations</p>
          <div className="space-y-2">
            {ltvCacRatio > 0 && ltvCacRatio < 1.5 && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 p-3 rounded-lg">
                <span className="font-bold flex-shrink-0">↑</span>
                <span>Your LTV:CAC is below 1.5:1. Consider reducing ad spend on low-converting channels or increasing average order value through upsells and bundles.</span>
              </div>
            )}
            {ltvCacRatio >= 1.5 && ltvCacRatio < 3 && (
              <div className="flex items-start gap-2 text-sm text-yellow-700 bg-yellow-50 p-3 rounded-lg">
                <span className="font-bold flex-shrink-0">→</span>
                <span>You're profitable per customer. To reach the 3:1 benchmark, focus on email retention campaigns and post-purchase upsells to increase repeat orders.</span>
              </div>
            )}
            {ltvCacRatio >= 3 && (
              <div className="flex items-start gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg">
                <span className="font-bold flex-shrink-0">✓</span>
                <span>Excellent unit economics. You could safely increase acquisition spend to grow faster — your LTV more than justifies higher CAC.</span>
              </div>
            )}
            {overview.repurchaseRate < 10 && overview.allTimeCustomers > 100 && (
              <div className="flex items-start gap-2 text-sm text-blue-700 bg-blue-50 p-3 rounded-lg">
                <span className="font-bold flex-shrink-0">→</span>
                <span>Repurchase rate is low. Most of your revenue comes from first-time buyers. A post-purchase email sequence or loyalty programme could significantly improve LTV.</span>
              </div>
            )}
            {overview.avgOrdersPerCustomer >= 2 && (
              <div className="flex items-start gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg">
                <span className="font-bold flex-shrink-0">✓</span>
                <span>Customers order {(overview.avgOrdersPerCustomer || 0).toFixed(1)}x on average — strong repeat purchase behaviour.</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// =============================================================================
// EXPORTS TAB
// =============================================================================
function ExportsTab({ storeId, userId, dateRange, customStart, customEnd }) {
  const [exporting, setExporting] = useState(null);

  const doExport = async (type) => {
    if (!storeId) { toast.error('Select a store first'); return; }
    setExporting(type);
    try {
      const { start, end } = dateRange === 'custom' && customStart && customEnd
        ? { start: customStart, end: customEnd }
        : getDateRange(dateRange);

      const res = await authFetch(`${API_BASE}?action=export&type=${type}&storeId=${storeId}&userId=${userId}&start=${start}&end=${end}`);

      if (!res.ok) {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const err = await res.json();
          throw new Error(err.error || `Server error ${res.status}`);
        }
        throw new Error(`Server error ${res.status}`);
      }

      const blob = await res.blob();
      if (blob.size === 0) throw new Error('No data in this date range');

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `revhapi-${type}-${start}-${end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded!');
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setExporting(null);
    }
  };

  const EXPORTS = [
    { type: 'orders',   label: 'Orders Report',       desc: 'All orders with revenue, costs, profit',  icon: ShoppingCart },
    { type: 'products', label: 'Product Analytics',   desc: 'Product-level P&L breakdown',             icon: Package },
    { type: 'pnl',      label: 'Daily P&L',           desc: 'Day-by-day profit & loss',               icon: TrendingUp },
    { type: 'customers',label: 'Customer Report',     desc: 'Customer list with CLV and LTV',          icon: Users },
  ];

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex gap-2">
        <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">Exports use the currently selected date range.</p>
      </div>
      {EXPORTS.map(({ type, label, desc, icon: Icon }) => (
        <div key={type} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4">
          <div className="p-3 bg-muted/30 rounded-lg flex-shrink-0"><Icon className="h-5 w-5 text-muted-foreground" /></div>
          <div className="flex-1">
            <p className="font-medium text-foreground text-sm">{label}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
          </div>
          <button onClick={() => doExport(type)} disabled={!!exporting}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-40">
            <Download className="h-4 w-4" />
            {exporting === type ? 'Exporting...' : 'CSV'}
          </button>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// CONFIGURATION SUB-COMPONENTS (each owns its own hooks at top level)
// =============================================================================
function ConfigStoreSettings({ storeId, userId }) {
 const [form, setForm] = useState({ transactionFeeType:'pct', transactionFee: 2.9, fixedFee: 0.30, currency:'USD', timezone:'UTC' });
 const [saved, setSaved] = useState(false);
 const [loadingSettings, setLoadingSettings] = useState(true);

 // Ad platform linking
 const [metaAccounts, setMetaAccounts]       = useState([]);
 const [googleAccounts, setGoogleAccounts]   = useState([]);
 const [metaCampaigns, setMetaCampaigns]     = useState([]);
 const [googleCampaigns, setGoogleCampaigns] = useState([]);
 const [storeLink, setStoreLink] = useState({
   adPlatform: 'none', metaAccountId: '', googleAccountId: '',
   selectedCampaigns: [], isPrimary: false,
 });
 const [savingLink, setSavingLink] = useState(false);
 const [loadingCampaigns, setLoadingCampaigns] = useState(false);

 useEffect(() => {
   if (!storeId || !userId) return;
   setLoadingSettings(true);

   // Load settings + store link + ad accounts in parallel
   Promise.all([
     authFetch(`${API_BASE}?action=get-settings&storeId=${storeId}&userId=${userId}`).then(r => r.json()).catch(() => ({})),
     authFetch(`${API_BASE}?action=stores&userId=${userId}`).then(r => r.json()).catch(() => ({})),
     supabase.from('meta_accounts').select('id,account_id,account_name').eq('user_id', userId),
     supabase.from('google_ads_accounts').select('id,customer_id,account_name').eq('user_id', userId),
   ]).then(([settingsD, storesD, metaRes, googleRes]) => {
     if (settingsD.success && settingsD.settings) setForm(f => ({ ...f, ...settingsD.settings }));
     const store = storesD.stores?.find(s => s.id === storeId);
     if (store) {
       setStoreLink({
         adPlatform:        store.adPlatform      || 'none',
         metaAccountId:     store.metaAccountId   || '',
         googleAccountId:   store.googleAdsAccountId || '',
         selectedCampaigns: store.selectedCampaigns || [],
         isPrimary:         store.isPrimary        || false,
       });
     }
     setMetaAccounts(metaRes.data   || []);
     setGoogleAccounts(googleRes.data || []);
   }).finally(() => setLoadingSettings(false));
 }, [storeId, userId]);

 // Load campaigns when account changes
 useEffect(() => {
   if (!storeLink.metaAccountId && !storeLink.googleAccountId) return;
   setLoadingCampaigns(true);
   Promise.all([
     storeLink.metaAccountId
       ? supabase.from('meta_campaigns').select('campaign_id,campaign_name,status').eq('meta_account_id', storeLink.metaAccountId)
       : Promise.resolve({ data: [] }),
     storeLink.googleAccountId
       ? supabase.from('google_ads_campaigns').select('campaign_id,campaign_name,status').eq('account_id', storeLink.googleAccountId).eq('excluded', false)
       : Promise.resolve({ data: [] }),
   ]).then(([metaRes, gRes]) => {
     setMetaCampaigns(metaRes.data || []);
     setGoogleCampaigns(gRes.data || []);
   }).finally(() => setLoadingCampaigns(false));
 }, [storeLink.metaAccountId, storeLink.googleAccountId]);

 const allCampaigns = [...metaCampaigns, ...googleCampaigns];
 const toggleCampaign = (id) => setStoreLink(l => ({
   ...l,
   selectedCampaigns: l.selectedCampaigns.includes(id)
     ? l.selectedCampaigns.filter(c => c !== id)
     : [...l.selectedCampaigns, id],
 }));

 const handleSaveLink = async () => {
   setSavingLink(true);
   try {
     const res = await authFetch(`${API_BASE}?action=update-store`, {
       method: 'POST', headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({
         userId, storeId,
         updates: {
           ad_platform:           storeLink.adPlatform === 'none' ? null : storeLink.adPlatform,
           meta_ads_account_id:   storeLink.metaAccountId   || null,
           google_ads_account_id: storeLink.googleAccountId || null,
           selected_campaigns:    storeLink.selectedCampaigns,
           is_primary:            storeLink.isPrimary,
         },
       }),
     });
     const d = await res.json();
     if (d.success) toast.success('Ad platform linked!');
     else toast.error(d.error || 'Save failed');
   } catch { toast.error('Save failed'); }
   setSavingLink(false);
 };

 const handleSave = async () => {
   try {
     const res = await authFetch(`${API_BASE}?action=update-settings`, {
       method:'POST', headers: {'Content-Type':'application/json' },
       body: JSON.stringify({ userId, storeId, settings: form }),
     });
     const d = await res.json();
     if (d.success) { setSaved(true); setTimeout(() => setSaved(false), 2000); toast.success('Settings saved!'); }
     else toast.error(d.error ||'Save failed');
   } catch { toast.error('Save failed'); }
 };

 if (loadingSettings) return (
   <div className="flex items-center gap-2 py-8 text-muted-foreground text-sm">
     <RefreshCw className="h-4 w-4 animate-spin" /> Loading settings...
   </div>
 );

 const showMeta   = storeLink.adPlatform === 'meta_ads'    || storeLink.adPlatform === 'both';
 const showGoogle = storeLink.adPlatform === 'google_ads'  || storeLink.adPlatform === 'both';

 return (
 <div className="max-w-2xl space-y-6">

   {/* ── Ad Platform Linking ─────────────────────────────────── */}
   <div className="bg-card rounded-xl border border-border p-6 space-y-5">
     <h3 className="font-semibold text-foreground">Ad Platform Linking</h3>
     <p className="text-sm text-muted-foreground -mt-2">Link your ad accounts so RevHapi can calculate ROAS against this store's revenue.</p>

     {/* Platform selector */}
     <div>
       <label className="block text-sm font-medium mb-2">Ad Platform</label>
       <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
         {[
           { v: 'none',       l: 'None' },
           { v: 'meta_ads',   l: 'Meta Ads' },
           { v: 'google_ads', l: 'Google Ads' },
           { v: 'both',       l: 'Both' },
         ].map(({ v, l }) => (
           <button key={v} onClick={() => setStoreLink(s => ({ ...s, adPlatform: v }))}
             className={`py-2 border rounded-lg text-sm font-medium transition-colors ${storeLink.adPlatform === v ? 'bg-primary text-white border-primary' : 'hover:border-primary/50'}`}>
             {l}
           </button>
         ))}
       </div>
     </div>

     {/* Meta account picker */}
     {showMeta && (
       <div>
         <label className="block text-sm font-medium mb-1">Meta Ads Account</label>
         <select value={storeLink.metaAccountId} onChange={e => setStoreLink(s => ({ ...s, metaAccountId: e.target.value, selectedCampaigns: [] }))}
           className="w-full px-3 py-2 border rounded-lg text-sm">
           <option value="">— Select account —</option>
           {metaAccounts.map(a => <option key={a.id} value={a.id}>{a.account_name} ({a.account_id})</option>)}
         </select>
       </div>
     )}

     {/* Google account picker */}
     {showGoogle && (
       <div>
         <label className="block text-sm font-medium mb-1">Google Ads Account</label>
         <select value={storeLink.googleAccountId} onChange={e => setStoreLink(s => ({ ...s, googleAccountId: e.target.value, selectedCampaigns: [] }))}
           className="w-full px-3 py-2 border rounded-lg text-sm">
           <option value="">— Select account —</option>
           {googleAccounts.map(a => <option key={a.id} value={a.id}>{a.account_name} ({a.customer_id})</option>)}
         </select>
       </div>
     )}

     {/* Campaign filter */}
     {allCampaigns.length > 0 && (
       <div>
         <div className="flex items-center justify-between mb-2">
           <label className="text-sm font-medium">Filter by campaigns</label>
           <div className="flex gap-2">
             <button onClick={() => setStoreLink(s => ({ ...s, selectedCampaigns: allCampaigns.map(c => c.campaign_id) }))}
               className="text-xs text-primary hover:underline">All</button>
             <button onClick={() => setStoreLink(s => ({ ...s, selectedCampaigns: [] }))}
               className="text-xs text-primary hover:underline">None</button>
           </div>
         </div>
         <p className="text-xs text-muted-foreground mb-2">Leave empty to include all campaigns. Select specific ones to filter ROAS by those campaigns only.</p>
         {loadingCampaigns ? (
           <div className="flex items-center gap-2 text-sm text-muted-foreground py-2"><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Loading campaigns...</div>
         ) : (
           <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
             {allCampaigns.map(c => {
               const selected = storeLink.selectedCampaigns.includes(c.campaign_id);
               return (
                 <div key={c.campaign_id} onClick={() => toggleCampaign(c.campaign_id)}
                   className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/30 cursor-pointer">
                   <div className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${selected ? 'bg-primary border-primary' : 'border-border'}`}>
                     {selected && <Check className="h-2.5 w-2.5 text-white" />}
                   </div>
                   <div className="flex-1 min-w-0">
                     <p className="text-sm truncate">{c.campaign_name}</p>
                     <p className="text-xs text-muted-foreground">{c.status}</p>
                   </div>
                 </div>
               );
             })}
           </div>
         )}
         {storeLink.selectedCampaigns.length > 0 && (
           <p className="text-xs text-muted-foreground mt-1">{storeLink.selectedCampaigns.length} campaign{storeLink.selectedCampaigns.length !== 1 ? 's' : ''} selected</p>
         )}
       </div>
     )}

     {/* No campaigns yet */}
     {(showMeta || showGoogle) && allCampaigns.length === 0 && !loadingCampaigns && (storeLink.metaAccountId || storeLink.googleAccountId) && (
       <p className="text-xs text-muted-foreground">No campaigns loaded yet. Go to Meta Ads or Google Ads and click "Campaigns" to load them first.</p>
     )}

     <button onClick={handleSaveLink} disabled={savingLink}
       className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50">
       {savingLink ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
       {savingLink ? 'Saving...' : 'Save Ad Platform Link'}
     </button>
   </div>

   {/* ── Transaction fee settings ─────────────────────────────── */}
   <div className="bg-card rounded-xl border border-border p-6 space-y-5">
     <h3 className="font-semibold text-foreground">Transaction Fee Settings</h3>
     <div>
       <label className="block text-sm font-medium mb-2">Fee Type</label>
       <div className="flex gap-2">
         {[{ v:'pct', l:'Percentage (%)' }, { v:'flat', l:'Flat Fee ($)' }, { v:'both', l:'Both' }].map(({ v, l }) => (
           <button key={v} onClick={() => setForm(f => ({ ...f, transactionFeeType: v }))}
             className={`flex-1 py-2 border rounded-lg text-sm ${form.transactionFeeType === v ?'bg-primary text-white border-primary' :''}`}>{l}</button>
         ))}
       </div>
     </div>
     {(form.transactionFeeType ==='pct' || form.transactionFeeType ==='both') && (
       <div>
         <label className="block text-sm font-medium mb-1">Percentage Fee (%)</label>
         <input type="number" step="0.1" value={form.transactionFee} onChange={e => setForm(f => ({ ...f, transactionFee: parseFloat(e.target.value) }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
       </div>
     )}
     {(form.transactionFeeType ==='flat' || form.transactionFeeType ==='both') && (
       <div>
         <label className="block text-sm font-medium mb-1">Fixed Fee per Order ($)</label>
         <input type="number" step="0.01" value={form.fixedFee} onChange={e => setForm(f => ({ ...f, fixedFee: parseFloat(e.target.value) }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
       </div>
     )}
   </div>

   <div className="bg-card rounded-xl border border-border p-6 space-y-4">
     <h3 className="font-semibold text-foreground">Store Preferences</h3>
     <div>
       <label className="block text-sm font-medium mb-1">Currency</label>
       <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
         {['USD','EUR','GBP','CAD','AUD','NGN'].map(c => <option key={c} value={c}>{c}</option>)}
       </select>
     </div>
     <div>
       <label className="block text-sm font-medium mb-1">Timezone</label>
       <select value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
         {['UTC','America/New_York','America/Los_Angeles','Europe/London','Africa/Lagos'].map(t => <option key={t} value={t}>{t}</option>)}
       </select>
     </div>
   </div>

   <button onClick={handleSave} className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90">
     {saved ? <CheckCircle className="h-4 w-4" /> : <Save className="h-4 w-4" />}
     {saved ?'Saved!' :'Save Settings'}
   </button>
 </div>
 );
}

function ConfigEmailReports() {
 const [form, setForm] = useState({ enabled: false, frequency:'weekly', dayOfWeek:'monday', hour:'08:00', email:'', includePnL: true, includeProducts: true, includeAdSpend: true });
 return (
 <div className="max-w-xl space-y-5">
 <div className="bg-card rounded-xl border border-border p-6 space-y-4">
 <div className="flex items-center justify-between">
 <h3 className="font-semibold text-foreground">Automated Email Reports</h3>
 <label className="flex items-center gap-2 cursor-pointer">
 <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} className="rounded" />
 <span className="text-sm">Enabled</span>
 </label>
 </div>
 <div><label className="block text-sm font-medium mb-1">Email Address</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="you@email.com" /></div>
 <div>
 <label className="block text-sm font-medium mb-1">Frequency</label>
 <select value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
 <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
 </select>
 </div>
 {form.frequency ==='weekly' && (
 <div>
 <label className="block text-sm font-medium mb-1">Day of Week</label>
 <select value={form.dayOfWeek} onChange={e => setForm(f => ({ ...f, dayOfWeek: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
 {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
 </select>
 </div>
 )}
 <div><label className="block text-sm font-medium mb-1">Send Time</label><input type="time" value={form.hour} onChange={e => setForm(f => ({ ...f, hour: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
 <div className="space-y-2">
 <p className="text-sm font-medium">Include in report:</p>
 {[['includePnL','P&L Summary'], ['includeProducts','Top Products'], ['includeAdSpend','Ad Spend Summary']].map(([k, l]) => (
 <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form[k] ?? true} onChange={e => setForm(f => ({ ...f, [k]: e.target.checked }))} className="rounded" />{l}</label>
 ))}
 </div>
 <button onClick={() => toast.success('Email report settings saved!')} className="w-full py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">
 Save Email Settings
 </button>
 </div>
 </div>
 );
}

function ConfigCustomEmailReports() {
 const [reports, setReports] = useState([]);
 const [form, setForm] = useState({ name:'', email:'', metrics: [], schedule:'weekly' });
 const METRICS = ['Revenue','Net Profit','ROAS','Ad Spend','Orders','Units Sold','New Customers','LTV'];
 return (
 <div className="max-w-xl space-y-5">
 <div className="bg-card rounded-xl border border-border p-6 space-y-4">
 <h3 className="font-semibold text-foreground">Create Custom Report</h3>
 <div><label className="block text-sm font-medium mb-1">Report Name</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="My Weekly Summary" /></div>
 <div><label className="block text-sm font-medium mb-1">Send To</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="team@company.com" /></div>
 <div>
 <p className="text-sm font-medium mb-2">Metrics to include:</p>
 <div className="grid grid-cols-2 gap-2">
 {METRICS.map(m => (
 <label key={m} className="flex items-center gap-2 text-sm">
 <input type="checkbox" checked={form.metrics.includes(m)} onChange={e => setForm(f => ({ ...f, metrics: e.target.checked ? [...f.metrics, m] : f.metrics.filter(x => x !== m) }))} className="rounded" />{m}
 </label>
 ))}
 </div>
 </div>
 <button
 onClick={() => { if (form.name && form.email) { setReports(r => [...r, { ...form, id: Date.now() }]); setForm({ name:'', email:'', metrics: [], schedule:'weekly' }); toast.success('Custom report created!'); } else toast.error('Fill name and email.'); }}
 className="w-full py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 flex items-center justify-center gap-2">
 <Plus className="h-4 w-4" /> Create Report
 </button>
 </div>
 {reports.map(r => (
 <div key={r.id} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between">
 <div><p className="font-medium text-sm text-foreground">{r.name}</p><p className="text-xs text-muted-foreground">{r.email} · {r.schedule} · {r.metrics.length} metrics</p></div>
 <button onClick={() => setReports(rs => rs.filter(x => x.id !== r.id))} className="p-1.5 text-red-400 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button>
 </div>
 ))}
 </div>
 );
}

function ConfigCalcPreferences() {
 const [prefs, setPrefs] = useState({ revenueIncludes:'gross', cogsMethod:'product-level', adAttribution:'blended', refundHandling:'deduct', taxHandling:'exclude' });
 return (
 <div className="max-w-xl space-y-5">
 <div className="bg-card rounded-xl border border-border p-6 space-y-5">
 <h3 className="font-semibold text-foreground">Calculation Preferences</h3>
 {[
 { key:'revenueIncludes', label:'Revenue includes', opts: [{ v:'gross', l:'Gross (before refunds)' }, { v:'net', l:'Net (after refunds)' }] },
 { key:'cogsMethod', label:'COGS method', opts: [{ v:'product-level', l:'Product-level (recommended)' }, { v:'blended', l:'Blended average' }] },
 { key:'adAttribution', label:'Ad attribution', opts: [{ v:'blended', l:'Blended (all channels)' }, { v:'last-click', l:'Last click' }, { v:'first-click', l:'First click' }] },
 { key:'refundHandling', label:'Refund handling', opts: [{ v:'deduct', l:'Deduct from revenue' }, { v:'separate', l:'Show as separate line' }] },
 { key:'taxHandling', label:'Tax handling', opts: [{ v:'exclude', l:'Exclude from revenue' }, { v:'include', l:'Include in revenue' }] },
 ].map(({ key, label, opts }) => (
 <div key={key}>
 <label className="block text-sm font-medium mb-2">{label}</label>
 <div className="flex gap-2 flex-wrap">
 {opts.map(({ v, l }) => (
 <button key={v} onClick={() => setPrefs(p => ({ ...p, [key]: v }))}
 className={`px-3 py-2 border rounded-lg text-sm ${prefs[key] === v ?'bg-primary text-white border-primary' :'hover:border-border'}`}>{l}</button>
 ))}
 </div>
 </div>
 ))}
 <button onClick={() => toast.success('Preferences saved!')} className="w-full py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 flex items-center justify-center gap-2">
 <Save className="h-4 w-4" /> Save Preferences
 </button>
 </div>
 </div>
 );
}

// Router — no hooks here, just picks the right component
function ConfigTab({ section, storeId, userId }) {
 if (section ==='config-store') return <ConfigStoreSettings storeId={storeId} userId={userId} />;
 if (section ==='config-email') return <ConfigEmailReports />;
 if (section ==='config-custom-email') return <ConfigCustomEmailReports />;
 if (section ==='config-calc') return <ConfigCalcPreferences />;
 return null;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function EcommerceDashboard() {
 const { user, isAdmin } = useAuth();
 const [searchParams, setSearchParams] = useSearchParams();
 const [activeSection, setActiveSection] = useState(() => searchParams.get('section') || 'dashboard');
 const [configOpen, setConfigOpen] = useState(false);
 const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // collapsed by default on all screen sizes
 // Sync section when main sidebar nav changes the URL param
 useEffect(() => {
 const s = searchParams.get('section');
 if (s && s !== activeSection) setActiveSection(s);
 }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
 const [stores, setStores] = useState([]);
 const [selectedStore, setSelectedStore] = useState(null);
 const [dateRange, setDateRange] = useState('30d');
 const [customStart, setCustomStart] = useState('');
 const [customEnd, setCustomEnd] = useState('');
 const [data, setData] = useState({});
 const [tiktokShopData, setTiktokShopData] = useState(null);   // dual-source overlay
 const [loading, setLoading] = useState(true);
 const [syncing, setSyncing] = useState(false);
 const [showDatePicker, setShowDatePicker] = useState(false);

 // Shared fetch-usage counter — same source as Overview and Publisher.
 const { used: feUsed, limit: feLimit, remaining: feRemaining, refresh: refreshFetchUsage } = useFetchUsage();
 const fetchUsage = { used: feUsed, limit: feLimit, remaining: feRemaining };

 // Load stores
 const loadStores = useCallback(async () => {
 if (!user?.id) return;
 try {
 const res = await authFetch(`${API_BASE}?action=stores&userId=${user.id}`);
 const d = await res.json();
 if (d.success && d.stores?.length > 0) {
 setStores(d.stores);
 if (!selectedStore) setSelectedStore(d.stores[0].id);
 }
 } catch { console.error('Failed to load stores'); }
 }, [user?.id]);

 // Load analytics data
 const loadData = useCallback(async () => {
 if (!selectedStore || !user?.id) { setLoading(false); return; }
 setLoading(true);
 try {
 // Attribution should always use the widest possible range so existing orders
 // always appear — they are grouped by channel regardless of date.
 const isAttribution = activeSection === 'attribution';
 const { start, end } = isAttribution
   ? { start: '2020-01-01', end: localDateStr(new Date()) }
   : dateRange === 'custom' && customStart && customEnd
     ? { start: customStart, end: customEnd }
     : getDateRange(dateRange);

 // Run Shopify analytics + TikTok Shop in parallel. TikTok Shop tolerates
 // a missing tiktok_shop_daily table (migration not yet applied) by
 // returning null — the dual-source UI just hides the second column then.
 const [res, ttResult] = await Promise.all([
   authFetch(`${API_BASE}?action=analytics&storeId=${selectedStore}&userId=${user.id}&start=${start}&end=${end}&section=${activeSection}`),
   supabase
     .from('tiktok_shop_daily')
     .select('date, gmv, orders, refunds, fees, net_revenue')
     .eq('user_id', user.id)
     .gte('date', start).lte('date', end)
     .order('date', { ascending: true })
     .then(r => r.data || [], () => []),
 ]);
 const d = await res.json();
 if (d.success) setData(d.data || {});

 // Aggregate TikTok Shop daily rows into a small summary the
 // DashboardTab can render alongside the Shopify numbers.
 const rows = ttResult || [];
 if (rows.length > 0) {
   const totals = rows.reduce((s, r) => ({
     gmv:     s.gmv     + (parseFloat(r.gmv) || 0),
     orders:  s.orders  + (parseInt(r.orders) || 0),
     refunds: s.refunds + (parseFloat(r.refunds) || 0),
     fees:    s.fees    + (parseFloat(r.fees) || 0),
     net:     s.net     + (parseFloat(r.net_revenue) || 0),
   }), { gmv: 0, orders: 0, refunds: 0, fees: 0, net: 0 });
   setTiktokShopData({ ...totals, daily: rows });
 } else {
   setTiktokShopData(null);
 }
 } catch { console.error('Failed to load data'); }
 finally { setLoading(false); }
 }, [selectedStore, user?.id, dateRange, customStart, customEnd, activeSection]);

 useEffect(() => { loadStores(); }, [loadStores]);
 useEffect(() => { loadData(); }, [loadData]);

 // Re-fetch when user returns to this tab after being away
 useVisibilityRefresh(useCallback(() => { loadStores(); loadData(); }, [loadStores, loadData]));

 const [syncDebug, setSyncDebug] = useState(null);
 const [diagnosing, setDiagnosing] = useState(false);
 const [diagnoseResult, setDiagnoseResult] = useState(null);

 const handleSync = async (fetchAll = false) => {
 if (!selectedStore) return;
 setSyncing(true);
 setSyncDebug(null);
 setDiagnoseResult(null);
 const tid = toast.loading('Fetching store data...');
 try {
 // Pass the current dashboard date range so fetch covers what the user is viewing
 const { start, end } = dateRange ==='custom' && customStart && customEnd
 ? { start: customStart, end: customEnd }
 : getDateRange(fetchAll ?'ytd' : dateRange);

 const body = { storeId: selectedStore, userId: user.id };
 if (!fetchAll) { body.startDate = start; body.endDate = end; }
 // fetchAll: no dates → API defaults to 90d, but we also try going back further
 if (fetchAll) {
 body.startDate = localDateStr(new Date(Date.now() - 365 * 86400000));
 body.endDate = localDateStr(new Date());
 }

 const res = await authFetch(`${API_BASE}?action=fetch`, {
 method:'POST', headers: {'Content-Type':'application/json' },
 body: JSON.stringify(body),
 });
 const d = await res.json();

 if (d.debug) setSyncDebug({ log: d.debug, error: d.success ? null : d.error, warnings: d.warnings });

 if (d.success) {
 const msg = `Fetched ${d.ordersCount || 0} orders → ${d.daysUpdated || 0} days updated`;
 const hasWarnings = d.warnings && (d.warnings.orderErrors > 0 || d.warnings.dailyErrors > 0);
 if (hasWarnings) {
 toast.success(`${msg} (${d.warnings.orderErrors + d.warnings.dailyErrors} warnings — see debug log)`, { id: tid, duration: 6000 });
 } else {
 toast.success(msg, { id: tid });
 }
 loadData();
 refreshFetchUsage();  // bump the shared counter — Overview/Publisher reflect it too
 } else {
 toast.error(d.error ||'Fetch failed — see debug log below', { id: tid, duration: 8000 });
 }
 } catch (e) {
 toast.error(`Fetch failed: ${e.message}`, { id: tid });
 setSyncDebug({ log: [`Client-side error: ${e.message}`], error: e.message });
 } finally {
 setSyncing(false);
 }
 };

 const handleDiagnose = async () => {
 if (!selectedStore) return;
 setDiagnosing(true);
 setDiagnoseResult(null);
 try {
 const res = await authFetch(`${API_BASE}?action=diagnose&storeId=${selectedStore}&userId=${user.id}`);
 const d = await res.json();
 setDiagnoseResult(d);
 } catch (e) {
 setDiagnoseResult({ success: false, checks: [{ label:'Connection', value: e.message, ok: false }] });
 } finally {
 setDiagnosing(false);
 }
 };

 const isConfig = activeSection.startsWith('config');
 const currentStore = stores.find(s => s.id === selectedStore);

 return (
 <div className="relative flex h-full overflow-hidden">


 {/* ── Main content ── */}
 <div className="flex-1 flex flex-col overflow-hidden">

 {/* ── Top bar: stores | date | fetch ── sticky below app header ── */}
 <div className="flex-shrink-0 px-5 pt-5 pb-3">
 <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
 {/* Left: section title */}
 <div className="flex-shrink-0 flex flex-col gap-0.5">
 <h1 className="text-base font-semibold text-foreground">
 {NAV.flatMap(n => n.children || [n]).find(n => n.key === activeSection)?.label || 'Dashboard'}
 </h1>
 {/* Last fetch for the selected store. */}
 {(() => {
   const sel = stores.find(s => s.id === selectedStore);
   return sel?.last_fetch || sel?.lastFetch
     ? <LastUpdated value={sel.last_fetch || sel.lastFetch} variant="inline" />
     : null;
 })()}
 </div>
 {!isConfig && (
 <div className="flex flex-wrap items-center gap-1.5">
 {/* Store selector */}
 {stores.length > 0 && (
 <select value={selectedStore ||''} onChange={e => setSelectedStore(e.target.value)}
 className="px-2 py-1.5 border border-border rounded-lg text-sm bg-card text-foreground max-w-[160px] truncate">
 {stores.map(s => <option key={s.id} value={s.id}>{s.shopDomain?.replace('.myshopify.com','')}</option>)}
 </select>
 )}
 {/* Date range — shared picker */}
 <DateRangePicker
   startDate={dateRange === 'custom' ? customStart : getPresetDates(dateRange).start}
   endDate={dateRange === 'custom' ? customEnd : getPresetDates(dateRange).end}
   defaultPreset={dateRange}
   showComparison={false}
   align="right"
   onChange={({ start, end, preset }) => {
     if (preset && preset !== 'custom') {
       setDateRange(preset);
       setCustomStart(''); setCustomEnd('');
     } else {
       setDateRange('custom');
       setCustomStart(start);
       setCustomEnd(end);
     }
     setShowDatePicker(false);
   }}
 />
 <button onClick={() => handleSync(false)} disabled={syncing || !selectedStore || fetchUsage.remaining <= 0}
 className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-40"
 title={fetchUsage.remaining <= 0 ? 'Daily fetch limit reached' : `${fetchUsage.remaining} fetches remaining today`}>
 <RefreshCw className={`h-3.5 w-3.5 ${syncing ?'animate-spin' :''}`} />
 {syncing ?'Fetching...' :'Fetch'}
 </button>
 <button onClick={() => handleSync(true)} disabled={syncing || !selectedStore || fetchUsage.remaining <= 0}
 className="flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-lg text-sm hover:bg-muted disabled:opacity-40 text-foreground"
 title={fetchUsage.remaining <= 0 ? 'Daily fetch limit reached' : 'Fetch all orders from the past 12 months'}>
 Fetch All
 </button>
 {/* Shared usage indicator — matches Overview and Publisher */}
 <span className="text-xs text-muted-foreground px-2 py-1 rounded-md bg-muted/40 whitespace-nowrap">
   {fetchUsage.used}/{fetchUsage.limit} today
 </span>
 </div>
 )}
 </div>
 </div>

 {/* ── Section tab bar ── */}
 {stores.length > 0 && !isConfig && (
   <div className="flex-shrink-0 border-b border-border overflow-x-auto">
     <div className="flex px-5 min-w-max">
       {NAV.map(n => (
         <button
           key={n.key}
           onClick={() => { setActiveSection(n.key); setSearchParams(n.key === 'dashboard' ? {} : { section: n.key }); }}
           className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap -mb-px ${
             activeSection === n.key
               ? 'border-primary text-primary'
               : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
           }`}
         >
           <n.icon className="h-4 w-4" />
           {n.label}
         </button>
       ))}
     </div>
   </div>
 )}

 {/* ── No store connected ── */}
 {stores.length === 0 && (
 <div className="flex-1 flex items-center justify-center">
 <div className="text-center max-w-sm">
 <Store className="h-12 w-12 text-muted-foreground/60 mx-auto mb-3" />
 <h2 className="font-semibold text-foreground mb-1">No Shopify store connected</h2>
 <p className="text-sm text-muted-foreground mb-4">Connect your store to start tracking P&L, products, and customer analytics.</p>
 <a href="/shopify" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90">
 <Plus className="h-4 w-4" /> Connect Shopify Store
 </a>
 </div>
 </div>
 )}

 {/* ── Content area ── */}
 {stores.length > 0 && (
 <div className="flex-1 overflow-y-auto p-5">

 {/* ── Diagnose result panel ── */}
 {diagnoseResult && (
 <div className="mb-4 rounded-xl border border-border bg-card overflow-hidden">
 <div className="flex items-center justify-between px-4 py-3 border-b">
 <span className="font-semibold text-sm text-foreground">🔍 Diagnostic Results</span>
 <button onClick={() => setDiagnoseResult(null)} className="text-muted-foreground hover:text-foreground text-base px-1">✕</button>
 </div>
 <div className="p-4 space-y-2">
 {(diagnoseResult.checks || []).map((c, i) => (
 <div key={i} className="flex items-center justify-between text-sm">
 <span className="text-muted-foreground">{c.label}</span>
 <span className={`font-medium ${c.ok ?'text-green-600' :'text-red-600'}`}>
 {c.ok ?'✓' :'✗'} {c.value}
 </span>
 </div>
 ))}
 {diagnoseResult.verdict && (
 <div className="mt-3 pt-3 border-t text-sm font-medium text-foreground">
 {diagnoseResult.verdict}
 </div>
 )}
 </div>
 </div>
 )}

 {/* ── Fetch debug log panel — admin only ── */}
 {isAdmin && syncDebug && (
 <div className={`mb-4 rounded-xl border text-xs font-mono ${syncDebug.error ?'bg-red-50 border-red-200' :'bg-muted/30 border-border'}`}>
 <div className="flex items-center justify-between px-3 py-2 border-b">
 <span className={`font-semibold text-sm ${syncDebug.error ?'text-red-700' :'text-foreground'}`}>
 {syncDebug.error ? `⚠ Fetch Error: ${syncDebug.error}` : '✓ Fetch Debug Log'} <span className="text-xs font-normal text-muted-foreground ml-1">(admin only)</span>
 {syncDebug.warnings && (syncDebug.warnings.orderErrors > 0 || syncDebug.warnings.dailyErrors > 0) && (
 <span className="ml-2 text-amber-600">
 ({syncDebug.warnings.orderErrors} order errors, {syncDebug.warnings.dailyErrors} daily errors)
 </span>
 )}
 </span>
 <button onClick={() => setSyncDebug(null)} className="text-muted-foreground hover:text-muted-foreground text-base leading-none px-1">✕</button>
 </div>
 <div className="p-3 max-h-64 overflow-y-auto space-y-0.5">
 {(syncDebug.log || []).map((line, i) => (
 <div key={i} className={`leading-relaxed ${line.includes('ERROR') || line.includes('FATAL') ?'text-red-600 font-semibold' : line.includes('WARNING') ?'text-amber-600' : line.includes('OK') || line.includes('COMPLETE') ?'text-green-700' :'text-muted-foreground'}`}>
 {line}
 </div>
 ))}
 </div>
 </div>
 )}

 {loading ? (
 <SkeletonEcommerce />
 ) : (
 <>
 {activeSection ==='dashboard' && <DashboardTab data={data} tiktokShop={tiktokShopData} syncing={syncing} onSync={handleSync} dateRange={dateRange} stores={stores} selectedStore={selectedStore} onStoreChange={setSelectedStore} />}
 {activeSection ==='products' && <ProductsTab data={data} />}
 {activeSection ==='orders' && <OrdersTab data={data} />}
 {activeSection ==='pnl' && <PnLTab data={data} />}
 {activeSection ==='attribution' && <AttributionTab data={data} />}
 {activeSection ==='clv' && <CLVTab data={data} />}
 {activeSection ==='exports' && <ExportsTab storeId={selectedStore} userId={user?.id} dateRange={dateRange} customStart={customStart} customEnd={customEnd} />}
 {isConfig && <ConfigTab section={activeSection} storeId={selectedStore} userId={user?.id} stores={stores} />}
 </>
 )}
 </div>
 )}
 </div>
 </div>
 );
}
